import React from 'react';
import { isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import { NumericTextBox, RadioButton } from '@progress/kendo-react-inputs';

import { GameContext } from '../../contexts/game';
import { updateGameSettings } from '../../services/games';
import { GameDocumentContext } from '../../contexts/game-document';
import { InGameSettings } from '../../types/game-document/settings';
import { DefaultInGame } from '../../types/game-document/settings/in-game';

export const Settings: React.FC = () => {
  const { gameCode } = useParams();
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [game] = React.useContext(GameContext);

  const [settings, setSettings] = React.useState<InGameSettings>(
    game.gameState?.runtime?.inGame ??
      gameDocument.gameDocument?.settings.inGame ??
      DefaultInGame
  );

  const isDisableChatSetting = React.useMemo(() => {
    const chatSetting = [
      settings.showInGameTeamChat,
      settings.showInGameGroupChat,
      settings.showInGameDirectChat,
      settings.showInGameAllPlayerChat,
      settings.showInGameFacilitatorChat
    ];
    return chatSetting.every((c) => c === false);
  }, [
    settings.showInGameTeamChat,
    settings.showInGameGroupChat,
    settings.showInGameDirectChat,
    settings.showInGameAllPlayerChat,
    settings.showInGameFacilitatorChat
  ]);

  const onChangeSettings = (name: string, value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  React.useEffect(() => {
    onChangeSettings('showInGameChat', !isDisableChatSetting);
  }, [isDisableChatSetting]);

  React.useEffect(() => {
    if (!gameCode || !game.gameState?.runtime?.inGame) return;
    // if we have not changed
    const hasChanged = !isEqual(game.gameState?.runtime?.inGame, settings);
    if (!hasChanged) return;

    // we have? push change
    updateGameSettings(gameCode, { inGame: settings });
  }, [gameCode, settings]);

  return (
    <div className={'in-game-settings container-fluid overflow-auto'}>
      <div
        className={'d-flex flex-column gap-1 align-items-start mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Menu + Functions</h2>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Change Player Name</span>
        <RadioButton
          name={'enableInGameChangePlayerName'}
          value={true}
          label={'Enable'}
          checked={settings && settings.enableInGameChangePlayerName}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'enableInGameChangePlayerName'}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          checked={settings && !settings.enableInGameChangePlayerName}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Enables players to change their name.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Info</span>
        <RadioButton
          name={'showInGameInfo'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameInfo}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameInfo'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameInfo}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of info tab in the menu.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Teams</span>
        <RadioButton
          name={'showInGameTeams'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameTeams}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameTeams'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameTeams}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of teams tab in menu. Teams tab shows the teams in the game
          <br />
          and each team's members.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Create Teams</span>
        <RadioButton
          name={'showInGameCreateTeams'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameCreateTeams}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameCreateTeams'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameCreateTeams}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Displayed on the team tab. Enables players to create teams.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Join Team/Leave Team</span>
        <RadioButton
          name={'showInGameJoinLeaveTeams'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameJoinLeaveTeams}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameJoinLeaveTeams'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameJoinLeaveTeams}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Displayed on the team tab. Enables players to join or leave a team.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Inventory</span>
        <RadioButton
          name={'showInGameInventory'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameInventory}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameInventory'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameInventory}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of inventory tab in the menu. Displays all available
          inventory, <br />
          player inventory and team inventory.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Titles</span>
        <RadioButton
          name={'showInGameRoles'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameRoles}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameRoles'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameRoles}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of titles tab in the menu. Displays all available titles,
          <br />
          player titles and team titles.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Scoreboard</span>
        <RadioButton
          name={'showInGameScoreboard'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameScoreboard}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameScoreboard'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameScoreboard}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of scoreboard in the menu. Displays player/team scores and
          leaderboard.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Player/Team score</span>
        <RadioButton
          name={'showInGamePlayerTeamScore'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGamePlayerTeamScore}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGamePlayerTeamScore'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGamePlayerTeamScore}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of the players score on scoreboard. In a team game,
          <br />
          displays the team's score and each team member's score.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Leaderboard</span>
        <RadioButton
          name={'showInGameLeaderboard'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameLeaderboard}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameLeaderboard'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameLeaderboard}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of leaderboard with ranking, names and scores of all
          players/teams <br /> on the scoreboard tab.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Chat</span>
        <RadioButton
          name={'showInGameChat'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameChat}
          disabled={isDisableChatSetting}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameChat'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameChat}
          disabled={isDisableChatSetting}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of chat in the menu.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Facilitator Chat</span>
        <RadioButton
          name={'showInGameFacilitatorChat'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameFacilitatorChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameFacilitatorChat'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameFacilitatorChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Enables a player to message the game controller.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>All Player Chat</span>
        <RadioButton
          name={'showInGameAllPlayerChat'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameAllPlayerChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameAllPlayerChat'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameAllPlayerChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Enables a player to message all players in the game.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Direct Chat</span>
        <RadioButton
          name={'showInGameDirectChat'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameDirectChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameDirectChat'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameDirectChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Enables a player to message another player.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Team Chat</span>
        <RadioButton
          name={'showInGameTeamChat'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameTeamChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameTeamChat'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameTeamChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Enables a player to message all players in a team.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Group Chat</span>
        <RadioButton
          name={'showInGameGroupChat'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameGroupChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameGroupChat'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameGroupChat}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Enables a player to message all players in a group.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Trade</span>
        <RadioButton
          name={'showInGameTrade'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameTrade}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameTrade'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameTrade}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Enables a player to trade with another player.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>On-Demand Task</span>
        <RadioButton
          name={'showInGameOnDemandTask'}
          value={true}
          label={'Show'}
          checked={settings && settings.showInGameOnDemandTask}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showInGameOnDemandTask'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showInGameOnDemandTask}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of on-demand task tab in the menu. The on-demand tab lists
          tasks that
          <br /> players can complete at any time during the game.
        </span>
      </div>

      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Tasks</h2>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Tasks require proximity</span>
        <RadioButton
          name={'tasksRequireProximity'}
          value={true}
          label={'True'}
          checked={settings && settings.tasksRequireProximity}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'tasksRequireProximity'}
          value={false}
          label={'False'}
          className={'ml-4'}
          checked={settings && !settings.tasksRequireProximity}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Player device must be within the set proximity before a task becomes
          available.
        </span>
      </div>

      <div>
        <div className={'d-flex gap-1 align-items-center mt-2 w-100'}>
          <span className={'mr-10 w-30'}>Set task proximity</span>
          <NumericTextBox
            name={'setTaskProximity'}
            className={'mr-1 w-15'}
            value={parseInt(settings.setTaskProximity ?? '')}
            onChange={(e) => {
              let value = e.value ?? 10;

              if (value <= 0) value = 10;
              setSettings((prev) => ({
                ...prev,
                setTaskProximity: value.toString()
              }));
            }}
            min={0}
            disabled={!settings.tasksRequireProximity}
          />
          <span className={'w-30'}>Meters</span>
        </div>

        <div className={'d-flex gap-1 align-items-center mb-2'}>
          <span className={'mb-2 in-game-description text-black-50'}>
            <span className={'material-symbols-outlined in-game-description'}>
              lightbulb
            </span>
            Set the proximity required before a task becomes available.
          </span>
        </div>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Pre-message text</span>
        <RadioButton
          name={'enablePremessageOutsideRange'}
          value={true}
          label={'True'}
          checked={settings && settings.enablePremessageOutsideRange}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'enablePremessageOutsideRange'}
          value={false}
          label={'False'}
          className={'ml-4'}
          checked={settings && !settings.enablePremessageOutsideRange}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Enable pre-message text when not in range.
        </span>
      </div>

      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Map Display</h2>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Other players/teams</span>
        <RadioButton
          name={'showOtherPlayerOrTeamOnMap'}
          value={true}
          label={'Show'}
          checked={settings && settings.showOtherPlayerOrTeamOnMap}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showOtherPlayerOrTeamOnMap'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showOtherPlayerOrTeamOnMap}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          Visibility of other players/teams on the map.{' '}
          <i>Note: A player's own icon is always visible.</i>
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Player score</span>
        <RadioButton
          name={'showPlayerScoreOnMap'}
          value={true}
          label={'Show'}
          checked={settings && settings.showPlayerScoreOnMap}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showPlayerScoreOnMap'}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={settings && !settings.showPlayerScoreOnMap}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          The player's score is displayed, beside the player icon.
        </span>
      </div>

      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Show detail on click</span>
        <RadioButton
          name={'showPlayerDetailByClickOnMap'}
          value={true}
          label={'On'}
          checked={settings && settings.showPlayerDetailByClickOnMap}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />

        <RadioButton
          name={'showPlayerDetailByClickOnMap'}
          value={false}
          label={'Off'}
          className={'ml-4'}
          checked={settings && !settings.showPlayerDetailByClickOnMap}
          onChange={(e) =>
            onChangeSettings(e.target.element?.name ?? '', e.value)
          }
        />
      </div>

      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 in-game-description text-black-50'}>
          <span className={'material-symbols-outlined in-game-description'}>
            lightbulb
          </span>
          When true, player icon is always on display. The icon is clicked to
          show, player name, <br />
          score, and team name. When false, player detail is always shown beside
          the player icon.
        </span>
      </div>
    </div>
  );
};
